import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  ScrollText, Lock, Download, Search, RefreshCw, ShieldAlert,
  Activity, AlertTriangle, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const ACTION_STYLES = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  login: 'bg-slate-100 text-slate-700',
  logout: 'bg-slate-100 text-slate-600',
  login_failed: 'bg-orange-100 text-orange-700',
};

const fmtTime = (ts) => {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};

const JsonBlock = ({ value, testid }) => (
  <pre
    data-testid={testid}
    className="bg-slate-900 text-slate-100 text-xs rounded-md p-3 overflow-auto max-h-72 whitespace-pre-wrap break-words"
  >
    {JSON.stringify(value, null, 2)}
  </pre>
);

const StatCard = ({ icon: Icon, label, value, tone, testid }) => (
  <Card className="p-4 flex items-center gap-3" data-testid={testid}>
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tone}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="text-2xl font-bold text-gray-900 leading-none">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  </Card>
);

const AuditLog = () => {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const canView = user?.is_superuser || roles.includes('System Manager') || roles.includes('Auditor');

  const [filters, setFilters] = useState({
    q: '', username: 'all', doctype: 'all', action: 'all', success: 'all',
    date_from: '', date_to: '',
  });
  const [options, setOptions] = useState({ usernames: [], doctypes: [], actions: [] });
  const [stats, setStats] = useState({ total: 0, failures: 0, today: 0 });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const buildParams = useCallback((extra = {}) => {
    const p = {};
    if (filters.q) p.q = filters.q;
    if (filters.username !== 'all') p.username = filters.username;
    if (filters.doctype !== 'all') p.doctype = filters.doctype;
    if (filters.action !== 'all') p.action = filters.action;
    if (filters.success !== 'all') p.success = filters.success === 'success';
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    return { ...p, ...extra };
  }, [filters]);

  const fetchLogs = useCallback(async (toPage = 0) => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = buildParams({ skip: toPage * PAGE_SIZE, limit: PAGE_SIZE });
      const { data } = await axios.get(`${API}/api/audit/logs`, { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(toPage);
    } catch (_) {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams, canView]);

  const fetchMeta = useCallback(async () => {
    if (!canView) return;
    try {
      const [{ data: opt }, { data: st }] = await Promise.all([
        axios.get(`${API}/api/audit/filters`),
        axios.get(`${API}/api/audit/stats`),
      ]);
      setOptions(opt);
      setStats(st);
    } catch (_) { /* ignore */ }
  }, [canView]);

  useEffect(() => {
    fetchMeta();
    fetchLogs(0);
    // eslint-disable-next-line
  }, []);

  const applyFilters = () => { fetchLogs(0); fetchMeta(); };

  const exportCsv = async () => {
    try {
      const { data } = await axios.get(`${API}/api/audit/export`, {
        params: buildParams(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_) { /* ignore */ }
  };

  if (!canView) {
    return (
      <Layout>
        <div className="p-10 text-center" data-testid="audit-access-denied">
          <Lock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-800">Access Denied</h2>
          <p className="text-sm text-gray-500">Only a System Manager or Auditor can view the audit log.</p>
        </div>
      </Layout>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Layout>
      <div className="p-6 max-w-[1500px] mx-auto" data-testid="audit-log-page">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <ScrollText className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
              <p className="text-sm text-gray-500">Every change, login and access decision across the system</p>
            </div>
          </div>
          <Button onClick={exportCsv} variant="outline" data-testid="audit-export-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard icon={Activity} label="Total events" value={stats.total} tone="bg-blue-100 text-blue-600" testid="audit-stat-total" />
          <StatCard icon={AlertTriangle} label="Failed / denied" value={stats.failures} tone="bg-orange-100 text-orange-600" testid="audit-stat-failures" />
          <StatCard icon={ShieldAlert} label="Today" value={stats.today} tone="bg-emerald-100 text-emerald-600" testid="audit-stat-today" />
        </div>

        <Card className="p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="audit-search-input"
                placeholder="Search user, path, record…"
                className="pl-9"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              />
            </div>
            <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
              <SelectTrigger data-testid="audit-filter-action"><SelectValue placeholder="Action" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {options.actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.doctype} onValueChange={(v) => setFilters({ ...filters, doctype: v })}>
              <SelectTrigger data-testid="audit-filter-doctype"><SelectValue placeholder="DocType" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All DocTypes</SelectItem>
                {options.doctypes.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.username} onValueChange={(v) => setFilters({ ...filters, username: v })}>
              <SelectTrigger data-testid="audit-filter-user"><SelectValue placeholder="User" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                {options.usernames.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.success} onValueChange={(v) => setFilters({ ...filters, success: v })}>
              <SelectTrigger data-testid="audit-filter-result"><SelectValue placeholder="Result" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All results</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failure">Failed / denied</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" data-testid="audit-filter-from" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
            <Input type="date" data-testid="audit-filter-to" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
            <Button onClick={applyFilters} data-testid="audit-apply-filters-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Apply
            </Button>
          </div>
        </Card>

        <Card className="overflow-hidden">
          <Table data-testid="audit-log-table">
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>DocType</TableHead>
                <TableHead>Record</TableHead>
                <TableHead>Endpoint</TableHead>
                <TableHead className="text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-gray-400">Loading…</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-gray-400" data-testid="audit-empty">No audit events match these filters.</TableCell></TableRow>
              ) : items.map((it) => (
                <TableRow
                  key={it.id}
                  className="cursor-pointer hover:bg-amber-50/60"
                  onClick={() => setSelected(it)}
                  data-testid={`audit-row-${it.id}`}
                >
                  <TableCell className="whitespace-nowrap text-xs text-gray-600">{fmtTime(it.timestamp)}</TableCell>
                  <TableCell className="font-medium text-gray-800">{it.username || '—'}</TableCell>
                  <TableCell><Badge className={`${ACTION_STYLES[it.action] || 'bg-gray-100 text-gray-700'} border-0`}>{it.action}</Badge></TableCell>
                  <TableCell className="text-gray-600">{it.doctype || '—'}</TableCell>
                  <TableCell className="text-gray-600">{it.record_id || '—'}</TableCell>
                  <TableCell className="text-xs text-gray-500 max-w-[260px] truncate">{it.method} {it.path}</TableCell>
                  <TableCell className="text-right">
                    <Badge className={`${it.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} border-0`}>{it.status_code}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <span className="text-xs text-gray-500" data-testid="audit-pagination-info">
              {total === 0 ? '0' : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)}`} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 0 || loading} onClick={() => fetchLogs(page - 1)} data-testid="audit-prev-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-gray-500">Page {page + 1} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages || loading} onClick={() => fetchLogs(page + 1)} data-testid="audit-next-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="audit-detail-sheet">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Badge className={`${ACTION_STYLES[selected.action] || 'bg-gray-100 text-gray-700'} border-0`}>{selected.action}</Badge>
                  <span className="text-base">{selected.doctype || selected.path}</span>
                </SheetTitle>
                <SheetDescription>{fmtTime(selected.timestamp)}</SheetDescription>
              </SheetHeader>

              <div className="mt-4 space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <div><span className="text-gray-400">User</span><div className="font-medium">{selected.username || '—'}</div></div>
                  <div><span className="text-gray-400">Roles</span><div className="font-medium">{(selected.roles || []).join(', ') || '—'}</div></div>
                  <div><span className="text-gray-400">Result</span><div><Badge className={`${selected.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} border-0`}>{selected.status_code} {selected.success ? 'OK' : 'Failed'}</Badge></div></div>
                  <div><span className="text-gray-400">Record</span><div className="font-medium">{selected.record_id || '—'}</div></div>
                  <div><span className="text-gray-400">IP</span><div className="font-medium">{selected.ip || '—'}</div></div>
                  <div><span className="text-gray-400">Duration</span><div className="font-medium">{selected.duration_ms ?? '—'} ms</div></div>
                  <div className="col-span-2"><span className="text-gray-400">Endpoint</span><div className="font-medium break-all">{selected.method} {selected.path}</div></div>
                </div>

                {Array.isArray(selected.changes) && selected.changes.length > 0 && (
                  <div data-testid="audit-detail-changes">
                    <div className="font-semibold text-gray-800 mb-2">Field changes</div>
                    <div className="border rounded-md divide-y">
                      {selected.changes.map((c) => (
                        <div key={c.field} className="grid grid-cols-3 gap-2 px-3 py-2 text-xs">
                          <div className="font-medium text-gray-700">{c.field}</div>
                          <div className="text-red-600 line-through break-words">{JSON.stringify(c.old)}</div>
                          <div className="text-emerald-700 break-words">{JSON.stringify(c.new)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selected.payload && (
                  <div>
                    <div className="font-semibold text-gray-800 mb-2">Submitted payload</div>
                    <JsonBlock value={selected.payload} testid="audit-detail-payload" />
                  </div>
                )}

                {selected.before && (
                  <div>
                    <div className="font-semibold text-gray-800 mb-2">Record before</div>
                    <JsonBlock value={selected.before} testid="audit-detail-before" />
                  </div>
                )}
                {selected.after && (
                  <div>
                    <div className="font-semibold text-gray-800 mb-2">Record after</div>
                    <JsonBlock value={selected.after} testid="audit-detail-after" />
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </Layout>
  );
};

export default AuditLog;
